// src/services/yahooFinance/yahooFinanceService.ts
import axios from 'axios';
import { YahooFinanceQuote, YahooFinanceNewsItem } from './yahooFinanceTypes';

interface YahooFinanceQuoteResponse {
  quoteResponse?: {
    result?: YahooFinanceQuote[];
  };
}

interface YahooFinanceChartResponse {
  chart?: {
    result?: any[];
  };
}

interface YahooFinanceNewsResponse {
  items?: YahooFinanceNewsItem[];
}

interface YahooFinanceSearchResponse {
  quotes?: any[];
  news?: any[];
  nav?: any[];
  lists?: any[];
}

/**
 * Service for interacting with Yahoo Finance API
 */
class YahooFinanceService {
  private apiKey: string | null = null;
  private baseUrl = '/api/yahoo'; // Point to your proxy instead of Yahoo directly
  
  async getNews(symbols?: string[], limit: number = 10): Promise<YahooFinanceNewsItem[]> {
    try {
      const params = new URLSearchParams();
      if (symbols && symbols.length > 0) {
        params.append('symbols', symbols.join(','));
      }
      params.append('limit', limit.toString());
  
      const response = await axios.get(`${this.baseUrl}/news`, { params });
      
      const data = response.data as YahooFinanceNewsResponse;
      if (!data || !Array.isArray(data.items)) {
        throw new Error('Invalid news response format');
      }
      
      return data.items.slice(0, limit);
    } catch (error) {
      console.error('Error fetching news via proxy:', error);
      throw error; // Re-throw so the UI knows there's an error
    }
  }
  
  /**
   * Set API key for Yahoo Finance (if using a proxy service that requires it)
   */
  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }
  
  /**
   * Get quotes for given symbols
   */
  async getQuotes(symbols: string[]): Promise<YahooFinanceQuote[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/quote`, {
        params: {
          symbols: symbols.join(','),
          modules: 'price,summaryDetail,defaultKeyStatistics',
          ...(this.apiKey ? { apiKey: this.apiKey } : {})
        }
      });
      
      // Validate response data
      const data = response.data as YahooFinanceQuoteResponse;
      if (!data || !data.quoteResponse || !data.quoteResponse.result) {
        throw new Error('Invalid response format from Yahoo Finance API');
      }
      
      return data.quoteResponse.result;
    } catch (error) {
      console.error('Error fetching quotes from Yahoo Finance:', error);
      throw error;
    }
  }
  
  /**
   * Get quote for a single symbol (wrapper around getQuotes for single symbol use)
   */
  async getQuote(symbol: string): Promise<YahooFinanceQuote> {
    const quotes = await this.getQuotes([symbol]);
    if (quotes.length === 0) {
      throw new Error(`No quote data available for ${symbol}`);
    }
    return quotes[0];
  }
  
  /**
   * Get historical data for a symbol
   */
  async getHistoricalData(symbol: string, interval: string = '1d', range: string = '1y'): Promise<any> {
    try {
      const response = await axios.get(`${this.baseUrl}/chart/${symbol}`, {
        params: {
          interval,
          range,
          ...(this.apiKey ? { apiKey: this.apiKey } : {})
        }
      });
      
      const data = response.data as YahooFinanceChartResponse;
      if (!data || !data.chart || !data.chart.result || data.chart.result.length === 0) {
        throw new Error('Invalid historical data response from Yahoo Finance');
      }
      
      return data.chart.result[0];
    } catch (error) {
      console.error(`Error fetching historical data for ${symbol}:`, error);
      throw error;
    }
  }
  
  /**
   * Search for symbols by query
   */
  async searchSymbols(query: string): Promise<any> {
    try {
      const response = await axios.get(`${this.baseUrl}/auto-complete`, {
        params: {
          q: query,
          region: 'US',
          lang: 'en',
          ...(this.apiKey ? { apiKey: this.apiKey } : {})
        }
      });
      
      const data = response.data as YahooFinanceSearchResponse;
      if (!data) {
        throw new Error('Invalid response format from Yahoo Finance API');
      }
      
      return data;
    } catch (error) {
      console.error('Error searching symbols from Yahoo Finance:', error);
      throw error;
    }
  }
  
  /**
   * Validate if a symbol exists
   */
  async validateSymbol(symbol: string): Promise<boolean> {
    try {
      const quotes = await this.getQuotes([symbol]);
      return quotes.length > 0;
    } catch (error) {
      console.error(`Error validating symbol ${symbol}:`, error);
      return false;
    }
  }
  
  /**
   * Get market indices data
   */
  async getMarketIndices(): Promise<any[]> {
    // Major market indices to fetch
    const indices = ['^GSPC', '^DJI', '^IXIC', '^TNX', '^VIX'];
    
    try {
      const quotes = await this.getQuotes(indices);
      return quotes.map(quote => ({
        name: this.getIndexName(quote.symbol),
        symbol: quote.symbol.replace('^', ''),
        value: quote.regularMarketPrice || 0,
        change: quote.regularMarketChange || 0,
        changePercent: quote.regularMarketChangePercent || 0,
      }));
    } catch (error) {
      console.error('Error fetching market indices:', error);
      return [];
    }
  }
  
  /**
   * Get human-readable name for index symbol
   */
  private getIndexName(symbol: string): string {
    const indexNames: Record<string, string> = {
      '^GSPC': 'S&P 500',
      '^DJI': 'Dow Jones',
      '^IXIC': 'Nasdaq',
      '^TNX': '10Y Treasury',
      '^VIX': 'Volatility Index'
    };
    
    return indexNames[symbol] || symbol;
  }
}

// Export singleton instance
export const yahooFinanceService = new YahooFinanceService();
